import { CommonModule, NgClass, NgIf } from '@angular/common';
import { Component, Inject, AfterViewInit, HostListener } from '@angular/core';
import { ExtendedModule, FlexModule } from '@angular/flex-layout';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import {
	MatDialogRef,
	MAT_DIALOG_DATA,
	MatDialog,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { Router } from '@angular/router';
import { ImageCroppedEvent, ImageCropperModule } from 'ngx-image-cropper';
import QRCodeStyling from 'qr-code-styling';
import { AccountService } from 'src/app/pages/account/services/account.service';
import { NotificationService } from '../../services/notification.service';
import { FundraiserService } from 'src/app/pages/fundraiser/services/fundraiser.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
	selector: 'app-qr-code-public',
	templateUrl: './qr-code-public.component.html',
	styleUrls: ['./qr-code-public.component.scss'],
	standalone: true,
	imports: [
		CommonModule,
		MatIconModule,
		ImageCropperModule,
		MatFormFieldModule,
		MatButtonToggleModule,
		FormsModule,
		MatButtonModule,
		FlexModule,
		NgIf,
		NgClass,
		ExtendedModule,
		MatProgressSpinnerModule
	],
})
export class QrCodePublicComponent implements AfterViewInit {
	urlToShare: string = this.data?.link || '';
	imageChangedEvent: any = '';
	logoPreviewImage!: string;
	custom_logo!: any;
	croppedImage: string = ''; // Store cropped image
	showCropper: boolean = false; // Determines whether to show the cropper
	currentLocale: string = '';
	whydonateLogo =
		'https://whydonate.com/cdn-cgi/imagedelivery/_0vgnXOEIHPwLg2E52a7gg/shared/whydonate_icon/public';
	qrCode: any; // QR code object

	selectedSize: number = 300; // Free Size for download
	isSizeSelected: boolean = false; // Tracks if the user has manually selected a size
	isFormatSelected: boolean = false;
	isAuthUserUpdate: boolean = false;
	previewSize: number = 300; // Keep preview fixed
	selectedFormat: string = 'png'; // Default File Format
	userSelectedFormat: string | null = null; // UI selection (null initially)
	userSelectedSize: number | null = null;
	isLoggedIn: any;
	isSaving: boolean = false;
	fundraiserId: string = '';
	localizedTitle: string = '';
	private isQRCodeInitialized = false;
	isLoading: Boolean = false;
	qrCodeId: string = '';

	constructor(
		public dialogRef: MatDialogRef<QrCodePublicComponent>,
		public fundraiserService: FundraiserService,
		@Inject(MAT_DIALOG_DATA)
		public data: {
			fundraiser: string;
			format: string;
			size: number;
			qrUrl: any;
			qrLogoUrl: any;
			link: string;
			details: any;
			qrFundraiserId: any;
			qrId: any;
		},
		private _router: Router,
		private dialog: MatDialog,
		private _accountService: AccountService, // Service to get account-related information
		public notificationService: NotificationService
	) {}

	ngOnInit(): void {
		this._accountService.getLoginInformation().subscribe((status) => {
			this.isLoggedIn = status;
		});
		this.currentLocale = this._accountService.getLocaleId();

		// Check if the component is opened from `QrCodePersonalComponent`
		if (this.data?.qrUrl && this.data?.qrLogoUrl) {
			// User is redirected from `QrCodePersonalComponent`
			this.qrCodeId = this.data.qrId;
			this.urlToShare = this.data.qrUrl;
			this.logoPreviewImage = this.data.qrLogoUrl;
			(this.userSelectedFormat = this.data.format),
				(this.userSelectedSize = this.data.size),
				this.onFormatChange(this.userSelectedFormat);
			this.onSizeChange(this.userSelectedSize);
			this.localizedTitle = this.data.fundraiser || 'No title available';
			this.fundraiserId = this.data.qrFundraiserId;
			this.isAuthUserUpdate = true;
		} else {
			// Default behavior
			this.logoPreviewImage = this.whydonateLogo;
			this.urlToShare = this.data?.link || '';
			this.selectedSize = 300;
			this.localizedTitle = '';
			this.fundraiserId = this.data?.details?.fundraiser?.id;
		}
		this.updatePreviewSize();
	}

	getLocalizedTitle(): string {
		const localeKey = `title_${this.currentLocale}`; // Construct the key dynamically
		const title =
			this.data?.details?.fundraiser?.translations?.[localeKey] ||
			this.data?.details?.fundraiser?.translations?.title_en ||
			this.localizedTitle ||
			'No title available';

		this.localizedTitle = title;
		return title;
	}

	// ✅ Listen for window resize events
	@HostListener('window:resize', [])
	onResize() {
		this.updatePreviewSize();
	}

	onSizeChange(size: number) {
		if (size) {
			this.selectedSize = size;
			this.isSizeSelected = true; // Mark that user has selected a size
			this.generateQRCode(); // ✅ Ensure the QR code updates when size changes
			this.isAuthUserUpdate = false;
		}
	}

	// ✅ Dynamically update preview size based on screen width
	updatePreviewSize() {
		this.previewSize = window.innerWidth < 425 ? 266 : 300;
		this.generateQRCode(); // Regenerate QR with new size
	}
	onFormatChange(format: string | null) {
		if (format) {
			this.selectedFormat = format; // Only update when user selects
			this.isFormatSelected = true;
			this.generateQRCode();
			this.isAuthUserUpdate = false;
		}
	}

	// This method is called once the component is initialized
	ngAfterViewInit() {
		if (!this.isQRCodeInitialized) {
			this.generateQRCode();
			this.isQRCodeInitialized = true;
		}
	}

	// Event triggered when file is selected
	fileChangeEvent(event: any): void {
		if (event.target.files && event.target.files.length > 0) {
			this.imageChangedEvent = event;
			this.showCropper = true; // Show cropper only if an image is selected
		} else {
			this.showCropper = false; // Keep the upload button if no file is chosen
		}
	}

	// Cropper event when user adjusts the image
	imageCropped(event: ImageCroppedEvent) {
		this.croppedImage = event.base64 ?? ''; // Ensure it's always a string
		this.custom_logo = this.croppedImage
			? this.dataURItoBlob(this.croppedImage)
			: null;
		this.isAuthUserUpdate = false;
	}

	// User confirms cropping
	cropImage() {
		this.showCropper = false;
		this.logoPreviewImage = this.croppedImage; // Update preview with cropped image

		if (this.croppedImage) {
			this.custom_logo = this.dataURItoBlob(this.croppedImage);
		} else {
			this.custom_logo = null; // Explicitly set to null if no cropped image
		}
		this.generateQRCode(); // Regenerate the QR code with the new logo
	}

	dataURItoBlob(dataURI: string): File | null {
		if (!dataURI.includes(',')) return null; // Ensure base64 format is valid

		const byteString = atob(dataURI.split(',')[1]);
		const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0]; // Extract MIME type
		const ab = new ArrayBuffer(byteString.length);
		const ia = new Uint8Array(ab);

		for (let i = 0; i < byteString.length; i++) {
			ia[i] = byteString.charCodeAt(i);
		}

		const blob = new Blob([ab], { type: mimeString });
		return new File([blob], 'custom_logo.png', { type: mimeString });
	}

	// Close dialog
	onCloseClick() {
		this.dialog.closeAll();
	}

	onBackClick() {
		this.dialogRef.close();
	}

	// Generate QR code using qr-code-styling library
	generateQRCode() {
		const logoUrl = this.logoPreviewImage || this.whydonateLogo;

		if (!this.qrCode) {
			this.qrCode = new QRCodeStyling({
				width: this.previewSize, // Keep preview size fixed
				height: this.previewSize,
				data: this.urlToShare,
				image: logoUrl,
				imageOptions: {
					crossOrigin: 'anonymous', // 🔹 Allow CORS for external images
					margin: 2, // ✅ Adds 2px padding around the logo
				},
				dotsOptions: { color: '#000000', type: 'square' },
				backgroundOptions: { color: '#ffffff' },
			});

			this.qrCode.append(document.getElementById('qr-code-preview')!);
		} else {
			this.qrCode.update({
				data: this.urlToShare,
				image: logoUrl,
				imageOptions: {
					crossOrigin: 'anonymous', // 🔹 Ensure this is set when updating QR
					margin: 2, // ✅ Adds 2px padding around the logo
				},
			});
		}
	}

	// ✅ Method to handle download with selected options
	downloadQRCode() {
		const logoUrl = this.logoPreviewImage || this.whydonateLogo;

		try {
			const qrCodeForDownload = new QRCodeStyling({
				width: this.selectedSize,
				height: this.selectedSize,
				data: this.urlToShare,
				image: logoUrl,
				imageOptions: {
					crossOrigin: 'anonymous',
					margin: 2, // ✅ Adds 2px padding around the logo
				},
				dotsOptions: { color: '#000000', type: 'square' },
				backgroundOptions: { color: '#ffffff' },
			});

			qrCodeForDownload.download({
				name: 'qr-code',
				extension: this.selectedFormat as 'png' | 'svg' | 'jpeg' | 'webp',
			});

			// ✅ Show success notification
			this.notificationService.openNotification(
				$localize`:@@qr_code_download_success:QR code downloaded successfully`,
				'OK',
				'success'
			);
		} catch (error) {
			// ❌ Show error notification
			this.notificationService.openNotification(
				$localize`:@@qr_code_download_error:Failed to download QR code`,
				'OK',
				'error'
			);
		}
	}

	routeToLogin() {
		this._router.navigate(['account/login']);
		this.dialog.closeAll();
	}

	uploadQRCodeData() {
		this.isSaving = true;
		let formData = new FormData();

		formData.append('fundraising_local_id', this.fundraiserId);
		formData.append('size', this.selectedSize.toString());
		formData.append('format', this.selectedFormat);
		formData.append('qr_url', this.urlToShare);
		formData.append('localized_title', this.getLocalizedTitle());

		if (this.custom_logo) {
			// ✅ User uploaded a new logo → Use `custom_logo`
			formData.append('qr_logo', this.custom_logo, 'qr_logo.png');
			this.uploadQRCode(formData);
		} else if (this.data?.qrUrl && this.data?.qrLogoUrl) {
			// ✅ User was redirected from `QrCodePersonalComponent` and did not change the logo
			const logoFile = this.dataURItoBlob(this.data.qrLogoUrl);
			if (logoFile) {
				formData.append('qr_logo', logoFile, 'qr_logo.png');
				this.uploadQRCode(formData);
			} else {
				// If `data.qrLogoUrl` is a URL, convert it to a file
				this.urlToFile(this.data.qrLogoUrl, 'qr_logo.png').then((file) => {
					formData.append('qr_logo', file, 'qr_logo.png');
					this.uploadQRCode(formData);
				});
			}
		} else {
			// ✅ Default behavior (Use `whydonateLogo`)
			this.urlToFile(this.whydonateLogo, 'default_logo.png').then((file) => {
				formData.append('qr_logo', file, 'default_logo.png');
				this.uploadQRCode(formData);
			});
		}
	}

	// ✅ Extracted function to handle the API call
	uploadQRCode(formData: FormData) {
		this.isLoading = true;
		if (!this.isLoggedIn) {
			this.urlToFile(this.whydonateLogo, 'default_logo.png').then((file) => {
				const reader = new FileReader();
				reader.readAsDataURL(file);
				reader.onloadend = () => {
					const base64Logo = reader.result as string;

					const qrSavePayload = {
						fundraising_local_id: this.fundraiserId,
						size: this.selectedSize,
						format: this.selectedFormat,
						qr_url: this.urlToShare,
						localized_title: this.getLocalizedTitle(),
						custom_logo: this.custom_logo ? this.croppedImage : base64Logo,
					};
					sessionStorage.setItem(
						'qrSavePayload',
						JSON.stringify(qrSavePayload)
					);

					// Redirect to login
					this._router.navigate(['/account/login']);
					this.dialog.closeAll();
				};
			});
			return;
		}

		// Proceed with upload if logged in
		this.fundraiserService.uploadQrData(formData).subscribe(
			(response: any) => {
				if (response?.status === 200) {
					this.notificationService.openNotification(
						$localize`:@@qr_code_save_success:QR Code saved successfully`,
						'OK',
						'success'
					);

					// ✅ After success, conditionally delete the previous QR silently
					if (this.data?.qrUrl && this.data?.qrLogoUrl && this.qrCodeId) {
						this.fundraiserService.deleteQr(this.qrCodeId).subscribe({
							next: () => {
								// Silent delete — no notification
								window.location.reload();
							},
							error: () => {
								// Even if delete fails, we still reload silently
								window.location.reload();
							},
						});
					} else {
						this._router.navigate(['/custom-branding/qrcode']);
					}

					this.isSaving = false;
					this.dialog.closeAll();
				}
			},
			(error) => {
				this.isSaving = false;
				this.notificationService.openNotification(
					$localize`:@@qr_code_save_error:Failed to save QR Code`,
					'OK',
					'error'
				);
			}
		);
	}

	urlToFile(url: string, filename: string): Promise<File> {
		return fetch(url)
			.then((res) => res.blob())
			.then((blob) => new File([blob], filename, { type: blob.type }));
	}
}
